import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { SpeedResult } from '../types/diagnostics';
import { formatSpeed } from '../utils/statistics';

interface Props {
  download: SpeedResult | null;
  upload: SpeedResult | null;
}

export const BandwidthChart: React.FC<Props> = ({ download, upload }) => {
  const downloadData = (download?.samples ?? []).map((s) => ({
    time: Math.round(s.elapsed / 1000 * 10) / 10,
    download: Math.round(s.speedMbps * 100) / 100,
  }));

  const uploadData = (upload?.samples ?? []).map((s) => ({
    time: Math.round(s.elapsed / 1000 * 10) / 10,
    upload: Math.round(s.speedMbps * 100) / 100,
  }));

  // Merge data on time axis
  const timeMap = new Map<number, { time: number; download?: number; upload?: number }>();

  for (const d of downloadData) {
    timeMap.set(d.time, { time: d.time, download: d.download });
  }
  for (const u of uploadData) {
    const existing = timeMap.get(u.time) ?? { time: u.time };
    existing.upload = u.upload;
    timeMap.set(u.time, existing);
  }

  const data = Array.from(timeMap.values()).sort((a, b) => a.time - b.time);

  return (
    <div className="bg-dark-900 rounded-xl p-6 border border-dark-700">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
          Bandwidth Curve
        </h3>
        <div className="flex gap-4 text-xs">
          {download && (
            <span className="text-emerald-400">
              ↓ {formatSpeed(download.speedMbps)}
            </span>
          )}
          {upload && (
            <span className="text-blue-400">
              ↑ {formatSpeed(upload.speedMbps)}
            </span>
          )}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id="dlGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="ulGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis
            dataKey="time"
            stroke="#475569"
            fontSize={11}
            tickLine={false}
            label={{ value: 'Time (s)', position: 'insideBottom', offset: -2, fill: '#64748b', fontSize: 10 }}
          />
          <YAxis
            stroke="#475569"
            fontSize={11}
            tickLine={false}
            label={{ value: 'Mbps', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }}
          />
          <Tooltip
            contentStyle={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            labelFormatter={(v) => `${v}s`}
            formatter={(v: number, name: string) => [
              `${v.toFixed(1)} Mbps`,
              name === 'download' ? 'Download' : 'Upload',
            ]}
          />
          <Area
            type="monotone"
            dataKey="download"
            stroke="#22c55e"
            fill="url(#dlGradient)"
            strokeWidth={2}
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="upload"
            stroke="#3b82f6"
            fill="url(#ulGradient)"
            strokeWidth={2}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Burst vs Sustained */}
      {download && (
        <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
          <div className="bg-dark-850 rounded-lg py-2 px-2">
            <div className="text-slate-500">Burst ↓</div>
            <div className="text-emerald-400 font-semibold">{formatSpeed(download.burstMbps)}</div>
          </div>
          <div className="bg-dark-850 rounded-lg py-2 px-2">
            <div className="text-slate-500">Sustained ↓</div>
            <div className="text-emerald-400 font-semibold">{formatSpeed(download.sustainedMbps)}</div>
          </div>
          {upload && (
            <>
              <div className="bg-dark-850 rounded-lg py-2 px-2">
                <div className="text-slate-500">Burst ↑</div>
                <div className="text-blue-400 font-semibold">{formatSpeed(upload.burstMbps)}</div>
              </div>
              <div className="bg-dark-850 rounded-lg py-2 px-2">
                <div className="text-slate-500">Sustained ↑</div>
                <div className="text-blue-400 font-semibold">{formatSpeed(upload.sustainedMbps)}</div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
